'use client';

import { useEffect, useReducer, useRef } from 'react';
import { bestKey } from '../funGames';
import { useGameTracking } from '../useGameTracking';
import { useLocalBest } from '../useLocalBest';
import ClickerChaos from './ClickerChaos';
import { costFor, type Effect, UPGRADES } from './clickerData';
const BEST = bestKey('clicker');

/** Passive income ticks four times a second so the counter feels alive. */
const TICK_MS = 250;
const TICKS_PER_SECOND = 1000 / TICK_MS;
type State = {
  stim: number;
  /** Highest total reached this run — spending must not lower your score. */
  peak: number;
  owned: Partial<Record<Effect, number>>;
  /** Floating "+n" markers; ids are a counter, never Math.random(). */
  pops: {
    id: number;
    amount: number;
    left: number;
  }[];
  nextPopId: number;
};
type Action = {
  type: 'click';
} | {
  type: 'tick';
} | {
  type: 'buy';
  id: Effect;
} | {
  type: 'expirePop';
  id: number;
} | {
  type: 'reset';
};
const initialState: State = {
  stim: 0,
  peak: 0,
  owned: {},
  pops: [],
  nextPopId: 1
};

/** Passive stimulation per second from everything owned. */
const rateOf = (owned: State['owned']) => UPGRADES.reduce((sum, up) => sum + (owned[up.id] ?? 0) * up.rate, 0);

/** Each click is worth more once you own more — otherwise clicking dies off. */
const clickValueOf = (owned: State['owned']) => 1 + Math.floor(rateOf(owned) / 12);
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'click':
      {
        const amount = clickValueOf(state.owned);
        const stim = state.stim + amount;
        return {
          ...state,
          stim,
          peak: Math.max(state.peak, stim),
          // Cap the markers so a rapid clicker can't grow the array unbounded.
          pops: [...state.pops.slice(-8), {
            id: state.nextPopId,
            amount,
            left: state.nextPopId * 29 % 80
          }],
          nextPopId: state.nextPopId + 1
        };
      }
    case 'tick':
      {
        const gained = rateOf(state.owned) / TICKS_PER_SECOND;
        if (gained === 0) return state;
        const stim = state.stim + gained;
        return {
          ...state,
          stim,
          peak: Math.max(state.peak, stim)
        };
      }
    case 'buy':
      {
        const upgrade = UPGRADES.find(up => up.id === action.id);
        if (!upgrade) return state;
        const owned = state.owned[action.id] ?? 0;
        if (owned >= upgrade.max) return state;
        const price = costFor(upgrade, owned);
        if (state.stim < price) return state;

        // Spending reduces `stim` but never `peak` — the score is what you earned,
        // not what you are holding, or buying anything would look like a penalty.
        return {
          ...state,
          stim: state.stim - price,
          owned: {
            ...state.owned,
            [action.id]: owned + 1
          }
        };
      }
    case 'expirePop':
      return {
        ...state,
        pops: state.pops.filter(pop => pop.id !== action.id)
      };
    case 'reset':
      return initialState;
    default:
      return state;
  }
};
const StimulationClicker = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const best = useLocalBest(BEST);
  const {
    record
  } = useGameTracking();
  const rate = rateOf(state.owned);
  const active = new Set(UPGRADES.filter(up => (state.owned[up.id] ?? 0) > 0).map(up_0 => up_0.id));

  // Passive income. Only runs once something has been bought.
  useEffect(() => {
    if (rate === 0) return;
    const id = setInterval(() => dispatch({
      type: 'tick'
    }), TICK_MS);
    return () => clearInterval(id);
  }, [rate]);

  /* Report the peak on an interval, not on every click — this is the one game
   * where "record whenever the score changes" would mean hundreds of writes a
   * minute.
   *
   * Both values are mirrored into refs so the interval can be created once. If
   * the effect depended on `peak` directly it would tear down and rebuild on
   * every single point earned: the 10s timer would never survive long enough to
   * fire, and the cleanup would flush on every change instead — exactly the
   * behaviour the interval exists to avoid. */
  const peakRef = useRef(0);
  const recordRef = useRef(record);
  peakRef.current = state.peak;
  recordRef.current = record;
  useEffect(() => {
    const flush = () => {
      if (peakRef.current > 0) {
        recordRef.current('clicker', Math.floor(peakRef.current));
      }
    };
    const id_0 = setInterval(flush, 10_000);

    // Also flush on unmount so a short session still counts.
    return () => {
      clearInterval(id_0);
      flush();
    };
  }, []);
  const shown = Math.floor(state.stim);
  return <div>
      {/* The stage. Everything the upgrades turn on is layered in here, over the
       * button but never blocking it. */}
      <div className={`relative min-h-[320px] overflow-hidden rounded-2xl border border-ds-border-standard bg-background-secondary p-6 ${active.has('shake') ? 'animate-blink' : ''}`}>
        <ClickerChaos active={active} counts={state.owned} />

        <div className="relative flex flex-col items-center justify-center gap-4 py-10">
          <p aria-live="off" className="text-5xl font-bold tabular-nums text-textPrimary sm:text-6xl">
            {shown.toLocaleString()}
          </p>
          <p className="text-sm text-textSecondary">
            stimulation
            {rate > 0 && <>
                {' · '}
                <span className="tabular-nums">
                  {rate.toLocaleString()}/sec
                </span>
              </>}
          </p>

          <div className="relative">
            <button type="button" onClick={() => dispatch({
            type: 'click'
          })} className="rounded-full bg-primary px-10 py-5 text-lg font-emphasis text-white transition-transform duration-100 hover:bg-indigo-400 active:scale-95">
              Click for stimulation
            </button>

            {/* Floating "+n" markers. */}
            {state.pops.map(pop => <span key={pop.id} onAnimationEnd={() => dispatch({
            type: 'expirePop',
            id: pop.id
          })} className="animate-fun-rise pointer-events-none absolute -top-2 text-sm font-emphasis text-primary" style={{
            left: `${pop.left}%`
          }}>
                +{pop.amount}
              </span>)}
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-baseline justify-between">
        <h2 className="font-emphasis text-textPrimary">Upgrades</h2>
        <button type="button" onClick={() => dispatch({
        type: 'reset'
      })} className="text-sm text-textGray underline underline-offset-2 transition-colors hover:text-textPrimary">
          Start over
        </button>
      </div>

      <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {UPGRADES.map(upgrade => {
        const owned = state.owned[upgrade.id] ?? 0;
        const maxed = owned >= upgrade.max;
        const price = costFor(upgrade, owned);
        const affordable = state.stim >= price;
        return <li key={upgrade.id} className="flex items-center gap-3 rounded-xl border border-ds-border-standard bg-background-secondary p-3">
              <span aria-hidden="true" className="text-2xl">
                {upgrade.emoji}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-emphasis text-textPrimary">
                  {upgrade.label}
                  {upgrade.max > 1 && owned > 0 && <span className="ml-1 text-textGray">×{owned}</span>}
                </span>
                <span className="block text-xs text-textSecondary">
                  {upgrade.blurb}
                </span>
                <span className="block text-xs text-textGray">
                  +{upgrade.rate}/sec
                </span>
              </span>
              <button type="button" disabled={maxed || !affordable} onClick={() => dispatch({
            type: 'buy',
            id: upgrade.id
          })} className="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-emphasis text-white transition-colors hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-30">
                {maxed ? 'Owned' : price.toLocaleString()}
              </button>
            </li>;
      })}
      </ul>

      {best !== null && <p className="mt-6 text-sm text-textGray">
          Personal best: {Number(best).toLocaleString()} stimulation
        </p>}
    </div>;
};
export default StimulationClicker;